import { useState, useMemo } from 'react'
import { Upload, Button, Table, Alert, message, Space, Typography, Modal, Select, Input, Tag, Statistic, Row, Col, Card } from 'antd'
import { InboxOutlined, ReloadOutlined, SaveOutlined, CheckCircleOutlined } from '@ant-design/icons'
import type { UploadProps } from 'antd'
import { uploadBedFund, saveBedFund } from '@/api/etl'
import { useBedFundPeriods, useCheckBedFundPeriod } from '@/hooks/useBedFund'
import type { BedFundUploadResponse, BedFundSaveResponse, BedFundRow } from '@/types/etl'

const { Dragger } = Upload
const { Title, Text } = Typography

const generateMonthOptions = () => {
  const months = [
    { value: '01', label: 'Январь' },
    { value: '02', label: 'Февраль' },
    { value: '03', label: 'Март' },
    { value: '04', label: 'Апрель' },
    { value: '05', label: 'Май' },
    { value: '06', label: 'Июнь' },
    { value: '07', label: 'Июль' },
    { value: '08', label: 'Август' },
    { value: '09', label: 'Сентябрь' },
    { value: '10', label: 'Октябрь' },
    { value: '11', label: 'Ноябрь' },
    { value: '12', label: 'Декабрь' },
  ]
  return months
}

const uslOkCodeColors: Record<string, string> = {
  'КС': 'blue',
  'ДСС': 'green',
  'ДСП': 'orange',
  'ДС': 'cyan',
}

export default function UploadBedFund() {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [uploadResult, setUploadResult] = useState<BedFundUploadResponse | null>(null)
  const [saveResult, setSaveResult] = useState<BedFundSaveResponse | null>(null)

  // Period selection
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null)
  const [isNewPeriod, setIsNewPeriod] = useState(false)
  const [newMonth, setNewMonth] = useState<string>('01')
  const [newYear, setNewYear] = useState<string>('26')
  const [saveModalOpen, setSaveModalOpen] = useState(false)
  const [confirmReplaceOpen, setConfirmReplaceOpen] = useState(false)

  // Fetch existing periods
  const { data: periodsData } = useBedFundPeriods()
  const periods = periodsData?.periods || []

  // Check if selected period has data
  const periodToCheck = isNewPeriod ? `${newMonth}${newYear}` : selectedPeriod
  const { data: periodCheck } = useCheckBedFundPeriod(periodToCheck)

  const effectivePeriod = useMemo(() => {
    return isNewPeriod ? `${newMonth}${newYear}` : selectedPeriod
  }, [isNewPeriod, newMonth, newYear, selectedPeriod])

  const handleUpload = async (file: File) => {
    setLoading(true)
    setSaveResult(null)
    setSelectedPeriod(null)
    setIsNewPeriod(false)
    try {
      const result = await uploadBedFund(file)
      setUploadResult(result)

      if (result.success) {
        message.success(`Загружено ${result.rows_count} записей коечного фонда`)
      } else {
        message.warning('Загрузка завершена с ошибками')
      }
    } catch (error: any) {
      message.error(`Ошибка загрузки: ${error.response?.data?.detail || error.message}`)
    } finally {
      setLoading(false)
    }
    return false
  }

  const handleSaveClick = () => {
    setSaveModalOpen(true)
  }

  const handlePeriodConfirm = () => {
    if (!effectivePeriod) {
      message.error('Выберите период')
      return
    }

    setSaveModalOpen(false)

    if (periodCheck?.exists) {
      setConfirmReplaceOpen(true)
    } else {
      doSave(false)
    }
  }

  const doSave = async (replaceExisting: boolean) => {
    if (!uploadResult || !effectivePeriod) return

    setSaving(true)
    setConfirmReplaceOpen(false)
    try {
      const result = await saveBedFund({
        period: effectivePeriod,
        rows: uploadResult.rows,
        replace_existing: replaceExisting,
      })
      setSaveResult(result)

      if (result.success) {
        message.success(`Сохранено: создано ${result.created_count}, обновлено ${result.updated_count}`)
      } else {
        message.warning(`Сохранено с ошибками: ${result.errors.length}`)
      }
    } catch (error: any) {
      message.error(`Ошибка сохранения: ${error.response?.data?.detail || error.message}`)
    } finally {
      setSaving(false)
    }
  }

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: '.xlsx',
    beforeUpload: handleUpload,
    showUploadList: false,
    disabled: loading,
  }

  const handleReset = () => {
    setUploadResult(null)
    setSaveResult(null)
    setSelectedPeriod(null)
    setIsNewPeriod(false)
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id_otd',
      key: 'id_otd',
      width: 80,
      fixed: 'left' as const,
    },
    {
      title: 'Отделение',
      dataIndex: 'otd_name',
      key: 'otd_name',
      ellipsis: true,
      width: 200,
    },
    {
      title: 'Тип',
      dataIndex: 'usl_ok_code',
      key: 'usl_ok_code',
      width: 70,
      render: (code: string) => code ? (
        <Tag color={uslOkCodeColors[code] || 'default'}>{code}</Tag>
      ) : '-',
    },
    {
      title: 'Профиль койки',
      dataIndex: 'profil_k',
      key: 'profil_k',
      ellipsis: true,
      width: 200,
    },
    {
      title: 'Профиль МП',
      dataIndex: 'profil_mp',
      key: 'profil_mp',
      ellipsis: true,
      width: 180,
    },
    {
      title: 'По приказу',
      dataIndex: 'kf_prikaz',
      key: 'kf_prikaz',
      width: 100,
      align: 'right' as const,
    },
    {
      title: 'Действующие',
      dataIndex: 'kf_fact',
      key: 'kf_fact',
      width: 100,
      align: 'right' as const,
    },
  ]

  // Period options for select
  const periodOptions = [
    ...periods.map((p) => ({
      value: p.period,
      label: `${p.period_label} (${p.records_count} записей, ${p.kf_fact} коек)`,
    })),
    { value: '__new__', label: '+ Добавить новый период' },
  ]

  return (
    <div>
      {!uploadResult ? (
        <Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Нажмите или перетащите файл для загрузки
          </p>
          <p className="ant-upload-hint">
            Поддерживается формат .xlsx (Отчёт "Структура коечного фонда")
          </p>
        </Dragger>
      ) : (
        <div>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Статистика */}
            <Row gutter={16}>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Записей"
                    value={uploadResult.rows_count}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Отделений"
                    value={uploadResult.unique_departments}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Коек по приказу"
                    value={uploadResult.total_kf_prikaz}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Коек действующих"
                    value={uploadResult.total_kf_fact}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
            </Row>

            {/* По типам */}
            {Object.keys(uploadResult.by_usl_ok).length > 0 && (
              <Space>
                <Text strong>По типам:</Text>
                {Object.entries(uploadResult.by_usl_ok).map(([type, count]) => (
                  <Tag key={type} color={uslOkCodeColors[type] || 'default'}>
                    {type}: {count} коек
                  </Tag>
                ))}
              </Space>
            )}

            {/* Результат сохранения */}
            {saveResult && (
              <Alert
                message={saveResult.success ? 'Успешно сохранено' : 'Сохранено с ошибками'}
                description={
                  <Space direction="vertical">
                    <Text>Создано: {saveResult.created_count}</Text>
                    <Text>Обновлено: {saveResult.updated_count}</Text>
                    <Text>Пропущено: {saveResult.skipped_count}</Text>
                    {saveResult.errors.length > 0 && (
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {saveResult.errors.slice(0, 5).map((e, i) => (
                          <li key={i}><Text type="danger">{e}</Text></li>
                        ))}
                      </ul>
                    )}
                  </Space>
                }
                type={saveResult.success ? 'success' : 'warning'}
                showIcon
                icon={saveResult.success ? <CheckCircleOutlined /> : undefined}
              />
            )}

            {/* Ошибки/предупреждения загрузки */}
            {uploadResult.errors.length > 0 && (
              <Alert
                message="Ошибки при загрузке"
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {uploadResult.errors.slice(0, 5).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                }
                type="error"
                showIcon
              />
            )}

            {uploadResult.warnings.length > 0 && (
              <Alert
                message="Предупреждения"
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {uploadResult.warnings.slice(0, 5).map((w, index) => (
                      <li key={index}>{w}</li>
                    ))}
                  </ul>
                }
                type="warning"
                showIcon
              />
            )}

            {/* Заголовок и кнопки */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Title level={4} style={{ margin: 0 }}>
                Загружено записей: {uploadResult.rows_count}
              </Title>
              <Space>
                {!saveResult && (
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving}
                    onClick={handleSaveClick}
                  >
                    Сохранить в базу
                  </Button>
                )}
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                >
                  Загрузить другой файл
                </Button>
              </Space>
            </div>

            {/* Таблица */}
            <Table
              columns={columns}
              dataSource={uploadResult.rows}
              rowKey={(row: BedFundRow) => `${row.id_otd}-${row.profil_k}`}
              pagination={{
                defaultPageSize: 50,
                showSizeChanger: true,
                pageSizeOptions: ['20', '50', '100', '200'],
                showTotal: (total, range) => `${range[0]}-${range[1]} из ${total}`,
              }}
              scroll={{ x: 1000 }}
              size="small"
            />
          </Space>
        </div>
      )}

      {/* Modal выбора периода */}
      <Modal
        title="Выберите период для сохранения"
        open={saveModalOpen}
        onOk={handlePeriodConfirm}
        onCancel={() => setSaveModalOpen(false)}
        okText="Продолжить"
        cancelText="Отмена"
        okButtonProps={{ disabled: !effectivePeriod }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text>
            Будет сохранено {uploadResult?.rows_count} записей
            ({uploadResult?.total_kf_fact} коек)
          </Text>

          <Select
            style={{ width: '100%' }}
            placeholder="Выберите период"
            value={isNewPeriod ? '__new__' : selectedPeriod}
            onChange={(value) => {
              if (value === '__new__') {
                setIsNewPeriod(true)
                setSelectedPeriod(null)
              } else {
                setIsNewPeriod(false)
                setSelectedPeriod(value)
              }
            }}
            options={periodOptions}
          />

          {isNewPeriod && (
            <Space>
              <Select
                style={{ width: 150 }}
                value={newMonth}
                onChange={setNewMonth}
                options={generateMonthOptions()}
              />
              <Input
                style={{ width: 80 }}
                placeholder="ГГ"
                value={newYear}
                onChange={(e) => setNewYear(e.target.value.replace(/\D/g, '').slice(0, 2))}
                maxLength={2}
                addonBefore="20"
              />
              <Text type="secondary">
                Период: {newMonth}{newYear}
              </Text>
            </Space>
          )}

          {periodCheck?.exists && !isNewPeriod && (
            <Alert
              message={`За ${periodCheck.period_label} уже есть ${periodCheck.records_count} записей`}
              type="warning"
              showIcon
            />
          )}
        </Space>
      </Modal>

      {/* Modal подтверждения замены */}
      <Modal
        title="Подтверждение замены данных"
        open={confirmReplaceOpen}
        onCancel={() => setConfirmReplaceOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setConfirmReplaceOpen(false)}>
            Отмена
          </Button>,
          <Button key="add" onClick={() => doSave(false)}>
            Добавить/обновить
          </Button>,
          <Button key="replace" type="primary" danger onClick={() => doSave(true)}>
            Заменить все
          </Button>,
        ]}
      >
        <Alert
          message={`За период ${periodCheck?.period_label} уже есть ${periodCheck?.records_count} записей`}
          description="Выберите действие:"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li><Text strong>Добавить/обновить</Text> — новые записи добавятся, существующие обновятся</li>
          <li><Text strong type="danger">Заменить все</Text> — существующие записи будут удалены</li>
        </ul>
      </Modal>
    </div>
  )
}
